import { useState } from 'react';
import { Icon } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import { PLATFORM_LABELS } from './shared/platformLabels.js';
import { api } from '../../services/api/client.js';
import { CreditConfirmationModal } from '../../components/CreditConfirmationModal.jsx';

export function StrategyOverviewTab({ strategy, setStrategy, onJumpTo }) {
  const [confirming, setConfirming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const inner = strategy?.strategy ?? null;
  const summary = inner?.summary || strategy?.summary;
  const positioning = inner?.positioning || strategy?.positioning;
  const insights = inner?.targetAudienceInsights || [];
  const pillars = inner?.contentPillars || (strategy?.contentPillars || []).map((name) => ({ name, purpose: 'Build trust' }));
  const recommendedTone = inner?.recommendedTone;
  const successMetrics = inner?.successMetrics || [];
  const platforms = strategy?.recommendedPlatforms || Object.keys(inner?.platformStrategy || {});
  const formats = strategy?.recommendedFormats || [];
  const platformStrategy = inner?.platformStrategy || {};
  const platformInstructions = inner?.platformInstructions || {};
  const quickSuggestionCount = Array.isArray(inner?.quickSuggestions) ? inner.quickSuggestions.length : 0;
  const strategicBullets = buildStrategicBullets({ positioning, pillars, recommendedTone, successMetrics, platforms, firstPost: inner?.firstPostRecommendation });

  const hasStrategy = !!strategy;

  async function generate(confirmCreditSpend, instruction = '') {
    const trimmedInstruction = instruction.trim();
    try {
      setGenerating(true);
      setGenerationError(null);
      const result = await api.strategy.generate({
        confirmCreditSpend,
        ...(trimmedInstruction ? { instruction: trimmedInstruction } : {})
      });
      setStrategy(result.strategy);
      notify('contentPlan.generated');
      setConfirming(false);
    } catch (err) {
      const msg = err.message || 'Content Plan generation failed';
      const errorCopy = contentPlanGenerationErrorCopy(err);
      setGenerationError(errorCopy);
      if (err?.code === 'CREDIT_REQUIRED' || err?.code === 'QUOTA_EXCEEDED' || err?.details?.reason === 'CREDIT_CONFIRMATION_REQUIRED' || /credit|quota/i.test(msg)) {
        setConfirming(true);
        notify('contentPlan.creditRequired', { description: errorCopy });
      } else {
        notify('contentPlan.generationFailed', { description: errorCopy });
      }
    } finally {
      setGenerating(false);
    }
  }

  if (!hasStrategy) {
    return (
      <div className="card ds-card-pad settings-card">
        <div className="settings-card-header">
          <h3 className="settings-card-title">No Content Plan yet</h3>
          <p className="settings-card-description">
            Generate a Content Plan so Create can suggest ideas, style, post length, CTAs, and references.
          </p>
        </div>
        <div className="settings-card-footer" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
          <span />
          <button className="btn btn-primary" onClick={() => generate(false)} disabled={generating}>
            <Icon name="sparkles" size={13} /> {generating ? 'Generating…' : 'Generate Content Plan'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="cp-module-shell">
        <div className="cp-module-hero cp-plan-hero">
          <div>
            <div className="settings-section-label">Content Plan active</div>
            <h3>Content Plan active</h3>
            <p>{summary || 'Dathent has enough guidance to suggest ideas, adapt posts by channel, and keep generated content on-brand.'}</p>
          </div>
          <div className="cp-plan-version">
            <span>Version</span>
            <strong>{strategy?.version ?? 1}</strong>
          </div>
        </div>

        <div className="cp-plan-card-grid">
          <PlanCard title="Ideas" body={quickSuggestionCount ? `${quickSuggestionCount} plan-backed suggestions ready in Create.` : 'Create uses this plan for relevant post ideas.'} icon="sparkles" />
          <PlanCard title="Channel style" body={platforms.length ? `Copy adapts for ${platforms.slice(0, 3).map(platformLabel).join(', ')}${platforms.length > 3 ? ', and more' : ''}.` : 'Each selected channel gets its own style.'} icon="layers" />
          <PlanCard title="Post length" body={firstContentLength(platformInstructions, platformStrategy) || 'Posts follow platform-native length and format guidance.'} icon="edit" />
          <PlanCard title="CTA" body={inner?.firstPostRecommendation?.cta || 'Posts include a clear next step when useful.'} icon="target" />
          <PlanCard title="References" body="Links, uploads, and brand references are used when relevant." icon="image" />
        </div>

        {strategicBullets.length > 0 && (
          <section className="cp-module-panel is-open">
            <div className="cp-module-body" style={{ paddingTop: 0 }}>
              <div className="settings-section-label" style={{ marginBottom: 8 }}>Simple strategy</div>
              <ul className="cp-bullet-list">
                {strategicBullets.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
          </section>
        )}

        <section className="cp-module-panel is-open">
          <button type="button" className="cp-module-trigger" onClick={() => setDetailsOpen((v) => !v)} aria-expanded={detailsOpen}>
            <span className="cp-node-dot is-ready" />
            <span className="cp-module-title-block">
              <strong>What AI is using</strong>
              <span>Brand inputs, channels, references, and content themes.</span>
            </span>
            <span className="cp-node-status">Ready</span>
            <Icon name={detailsOpen ? 'chev_u' : 'chev_d'} size={14} />
          </button>
          {detailsOpen && (
            <div className="cp-module-body">
              {insights.length > 0 && <Section title="Audience insights"><ul className="cp-bullet-list">{insights.map((line, i) => <li key={i}>{line}</li>)}</ul></Section>}
              {pillars.length > 0 && <Section title="Themes"><div className="cp-chip-row">{pillars.slice(0, 6).map((pillar, i) => <span key={`${pillar.name}-${i}`} className="chip chip-violet" title={pillar.purpose || ''}>{pillar.name}</span>)}</div></Section>}
              {platforms.length > 0 && <Section title="Channels"><div className="cp-chip-row">{platforms.map((id) => <span key={id} className="chip cp-platform-chip"><PlatformIcon platform={id} size={12} withColor={false} />{PLATFORM_LABELS[id] || id}</span>)}</div></Section>}
              {formats.length > 0 && <Section title="Formats"><div className="cp-chip-row">{formats.map((f, i) => <span key={i} className="chip">{String(f).replaceAll('_', ' ')}</span>)}</div></Section>}
              {recommendedTone && <Section title="Voice"><p className="body" style={{ margin: 0 }}>{recommendedTone}</p></Section>}
            </div>
          )}
        </section>

        <div className="cp-sticky-savebar">
          <button className="btn btn-secondary" onClick={() => { setGenerationError(null); setConfirming(true); }} disabled={generating}>
            <Icon name="sparkles" size={12} /> Regenerate
          </button>
          <button className="btn btn-ghost" onClick={() => onJumpTo?.('brand')}>
            Adjust inputs <Icon name="arrow_r" size={12} />
          </button>
          <button className="btn btn-ghost" onClick={() => onJumpTo?.('rules')}>
            Publishing rules <Icon name="arrow_r" size={12} />
          </button>
        </div>
      </div>

      {confirming && (
        <ConfirmRegenerateModal
          generating={generating}
          error={generationError}
          onCancel={() => { setConfirming(false); setGenerationError(null); }}
          onConfirm={(instruction) => generate(true, instruction)}
        />
      )}
    </>
  );
}

function PlanCard({ title, body, icon, children }) {
  return (
    <article className="cp-plan-card">
      <span className="cp-section-icon" aria-hidden="true"><Icon name={icon} size={15} /></span>
      <div>
        <div className="settings-section-label">{title}</div>
        {body && <p>{body}</p>}
        {children}
      </div>
    </article>
  );
}

function buildStrategicBullets({ positioning, pillars, recommendedTone, successMetrics, platforms, firstPost }) {
  const bullets = [];
  if (positioning) bullets.push(`Position the brand around: ${positioning}`);
  if (Array.isArray(pillars) && pillars.length) bullets.push(`Focus content on ${pillars.slice(0, 3).map((pillar) => pillar.name).filter(Boolean).join(', ')}.`);
  if (recommendedTone) bullets.push(`Keep the voice ${recommendedTone}.`);
  if (firstPost?.topic) bullets.push(`Start with a clear next post: ${firstPost.topic}.`);
  if (Array.isArray(platforms) && platforms.length) bullets.push(`Adapt posts for ${platforms.slice(0, 3).map(platformLabel).join(', ')}${platforms.length > 3 ? ', and more' : ''}.`);
  if (Array.isArray(successMetrics) && successMetrics.length) bullets.push(`Watch ${successMetrics.slice(0, 2).join(' and ')} to learn what works.`);
  return bullets.filter(Boolean).slice(0, 5);
}

function firstContentLength(platformInstructions, platformStrategy) {
  const sources = [...Object.values(platformInstructions || {}), ...Object.values(platformStrategy || {})];
  const match = sources.find((item) => item?.contentLength);
  return match?.contentLength || null;
}

function platformLabel(platform) {
  return PLATFORM_LABELS[platform] || platform;
}

export function contentPlanGenerationErrorCopy(error) {
  const reason = error?.details?.reason;
  const traceId = error?.details?.traceId;
  const aiCode = error?.details?.aiCode;
  let message = '';
  if (reason === 'CREDIT_CONFIRMATION_REQUIRED' || reason === 'INSUFFICIENT_CREDITS' || error?.code === 'QUOTA_EXCEEDED' || /credit|quota/i.test(error?.message || '')) {
    message = 'Plan quota is needed to generate this plan.';
  } else if (error?.code === 'INTERNAL_ERROR' && /setup|configured|api key/i.test(error?.message || '')) {
    message = 'AI setup needs attention before generating this plan.';
  } else if (aiCode === 'AI_PROVIDER_MALFORMED_JSON' || aiCode === 'AI_PROVIDER_INVALID_RESPONSE') {
    message = 'AI returned an invalid plan response. Try again in a moment.';
  } else if (error?.status === 0) {
    message = 'Check your connection and try again.';
  } else if (aiCode === 'AI_PROVIDER_NETWORK_ERROR' || aiCode === 'AI_PROVIDER_TIMEOUT' || error?.code === 'NETWORK_ERROR') {
    message = 'AI provider did not respond in time. Try again in a moment.';
  } else if (error?.status === 401 || error?.status === 403) {
    message = 'Your session needs a refresh. Sign in again and retry.';
  } else if (error?.status === 429 || error?.code === 'RATE_LIMITED') {
    message = 'Dathent is busy right now. Try again in a moment.';
  } else {
    message = 'I could not generate the plan right now. Try again in a moment.';
  }
  return traceId ? `${message} Trace ID: ${traceId}` : message;
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div className="settings-section-label" style={{ marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

export function ConfirmRegenerateModal({ generating, error, onCancel, onConfirm, creditCost = 10 }) {
  const [instruction, setInstruction] = useState('');

  return <CreditConfirmationModal
    open
    title="Regenerate Content Plan?"
    description="This refreshes the plan that powers ideas, style, CTAs, references, and channel-specific posts."
    creditCost={creditCost}
    confirming={generating}
    confirmLabel="Regenerate plan"
    confirmingLabel="Regenerating..."
    onCancel={onCancel}
    onConfirm={() => onConfirm?.(instruction)}
    onBuyCredits={() => { window.location.href = '/billing'; }}
    onUpgrade={() => { window.location.href = '/billing'; }}
  >
    <div style={{ marginTop: 14 }}>
      <label className="label" htmlFor="regenerate-plan-instruction">What should Dathent focus on this time?</label>
      <textarea
        id="regenerate-plan-instruction"
        className="textarea"
        value={instruction}
        onChange={(event) => setInstruction(event.target.value)}
        placeholder="Optional: sharpen the offer, focus on LinkedIn, make ideas more educational..."
        disabled={generating}
      />
      <div className="helper">Optional. Leave blank to refresh from current Brand Profile and rules.</div>
      {error && <div className="auth-error" role="alert" style={{ marginTop: 12 }}><Icon name="alert" size={13} /> {error}</div>}
    </div>
  </CreditConfirmationModal>;
}
